import { useI18n } from "@core/i18n/i18n";
import styled from "@emotion/styled";
import { ContentWithTooltip } from "@ui/components/common/content-with-tooltip";
import { colors } from "@ui/style/colors";
import { Panel as PrimePanel, PanelProps as PrimePanelProps } from "primereact/panel";
import React, { useMemo } from "react";
import ChevronDown from "@assets/icons/chevron-down.svg";
import ChevronUp from "@assets/icons/chevron-up.svg";
import { row } from "@ui/style";

const VARIANT_DEFAULT = "default";
const VARIANT_COMPACT = "compact";

interface BasePanelProps extends PrimePanelProps {
	headerText: React.ReactNode | string;
	tooltip?: React.ReactNode | string;
	showToggleButton?: boolean;
}

interface PanelProps extends BasePanelProps {
	variant?: typeof VARIANT_DEFAULT | typeof VARIANT_COMPACT;
}

export const Panel: React.FC<PanelProps> = ({
	variant = VARIANT_DEFAULT,
	showToggleButton = true,
	toggleable = true,
	children,
	...otherProps
}) =>
	useMemo(() => {
		const PanelComponent = variant === VARIANT_COMPACT ? CompactPanel : DefaultPanel;
		return (
			<PanelComponent showToggleButton={showToggleButton} toggleable={toggleable} {...otherProps}>
				{children}
			</PanelComponent>
		);
	}, [children, otherProps, showToggleButton, toggleable, variant]);

const CompactPanel: React.FC<BasePanelProps> = ({
	headerText,
	tooltip,
	showToggleButton = false,
	children,
	...otherProps
}) => {
	const header = useMemo(
		() => <ContentWithTooltip tooltip={tooltip}>{headerText}</ContentWithTooltip>,
		[headerText, tooltip],
	);
	const showIcon = useMemo(() => Boolean(children && showToggleButton), [children, showToggleButton]);

	return (
		<CompactStyledPanel
			showToggleButton={showToggleButton && showIcon}
			expandIcon={
				<CompactToggleText className="p-size-s-bold">
					{header}
					{showIcon ? <ChevronDown className="compact-panel-header-icon" width={24} height={24} /> : null}
				</CompactToggleText>
			}
			collapseIcon={
				<CompactToggleText className="p-size-s-bold">
					{header}
					{showIcon ? <ChevronUp className="compact-panel-header-icon" width={24} height={24} /> : null}
				</CompactToggleText>
			}
			{...otherProps}
		>
			{children}
		</CompactStyledPanel>
	);
};

const DefaultPanel: React.FC<BasePanelProps> = ({
	headerText,
	tooltip,
	showToggleButton = false,
	children,
	...otherProps
}) => {
	const { format } = useI18n();
	const showIcon = useMemo(() => Boolean(children && showToggleButton), [children, showToggleButton]);

	return (
		<StyledPanel
			showToggleButton={showToggleButton && showIcon}
			header={
				<ContentWithTooltip text={typeof headerText === "string" ? headerText : undefined} tooltip={tooltip}>
					{typeof headerText !== "string" ? headerText : undefined}
				</ContentWithTooltip>
			}
			expandIcon={
				showIcon ? (
					<ToggleText className="p-size-s-bold">{format("patientDetails.patientPrograms.panel.show")}</ToggleText>
				) : null
			}
			collapseIcon={
				showIcon ? (
					<ToggleText className="p-size-s-bold">{format("patientDetails.patientPrograms.panel.hide")}</ToggleText>
				) : null
			}
			{...otherProps}
		>
			{children}
		</StyledPanel>
	);
};

const StyledPanel = styled(PrimePanel)<{ showToggleButton: boolean }>`
	.p-panel-header-icon {
		display: ${({ showToggleButton }) => (showToggleButton ? "default" : "none")};
	}
`;

const ToggleText = styled.span`
	color: ${colors.grey};

	:hover {
		color: inherit;
	}
`;

const CompactStyledPanel = styled(StyledPanel)`
	& .p-panel-header {
		border-bottom: none !important;
		justify-content: flex-start;
	}
`;

const CompactToggleText = styled.div`
	${row("XS", "flexstart", "center")};

	&,
	& .compact-panel-header-icon {
		color: ${colors.primary};
	}

	&:hover,
	&:hover .compact-panel-header-icon {
		color: ${colors.primaryDark50};
	}

	&:active,
	&:active .compact-panel-header-icon {
		color: ${colors.primaryDark};
	}
`;
